import React from 'react';
import cn from 'classnames';
import Link from 'next/link';
import Image from 'next/image';

const Accordion = ({ title, items, show, slug, index, toggle }) => (
  <>
    <div
      className="flex items-center gap-3 text-base cursor-pointer select-none text-neutral-900 dark:text-neutral-300 group"
      onClick={() => toggle(index, show)}
    >
      <span className="flex p-2 border rounded-lg border-zinc-200 dark:border-zinc-800">
        {iconList[title] || placeholderIcon}
      </span>
      <p className="duration-300 transition-color group-hover:opacity-80 whitespace-nowrap">
        {title}
      </p>
    </div>
    <div
      className="h-auto overflow-auto transition-all duration-500 ease-in-out"
      style={{ maxHeight: !show ? '0px' : '9999px' }}
    >
      <ul className="pt-2 pl-[50px]">
        {Array.isArray(items) &&
          items.map(item => (
            <li
              className={cn(
                'group',
                item.slug !== 'scale-api-reference' ? 'mb-3 last:mb-0' : 'mb-0',
                {
                  hidden: item.hidden
                }
              )}
              key={item.slug}
            >
              {item.slug !== 'scale-api-reference' && (
                <Link
                  href={`/docs/${item.slug}`}
                  className="relative text-sm font-normal text-neutral-800 dark:text-neutral-400"
                >
                  <span
                    className={cn(
                      'w-4 absolute group-hover:opacity-100 transition-opacity duration-300',
                      item.slug !== slug ? 'opacity-0' : ''
                    )}
                    style={{ left: '-24px', top: 'calc(50% - 16px / 2)' }}
                  >
                    {arrowIcon}
                  </span>
                  <span
                    className={cn(
                      'transition-color duration-300',
                      item.slug !== slug
                        ? 'group-hover:opacity-80'
                        : 'text-neutral-900 dark:text-neutral-300 group-hover:opacity-100'
                    )}
                  >
                    {item.title}
                  </span>
                </Link>
              )}
              <ul className="mt-3 ml-6">
                {item.children.map(sub => (
                  <li className="mb-3" key={sub.slug}>
                    <Link
                      href={`/docs/${sub.slug}`}
                      className="relative text-sm font-normal text-neutral-800 dark:text-neutral-400"
                    >
                      <span
                        className={cn(
                          'w-4 absolute transition-opacity duration-300',
                          sub.slug !== slug ? 'hidden' : ''
                        )}
                        style={{ left: '-24px', top: 'calc(50% - 16px / 2)' }}
                      >
                        {arrowIcon}
                      </span>
                      <span
                        className={cn(
                          'transition-color duration-300',
                          sub.slug !== slug
                            ? 'hover:opacity-80'
                            : 'text-neutral-900 dark:text-neutral-300 hover:opacity-100'
                        )}
                      >
                        {sub.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
      </ul>
    </div>
  </>
);

export const arrowIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className="fill-current text-neutral-900 dark:text-neutral-300"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9.60684 12.1142C9.41154 12.3097 9.0947 12.3099 8.89917 12.1146C8.70363 11.9193 8.70344 11.6025 8.89874 11.4069L11.804 8.50522H3.00244C2.7263 8.50522 2.50244 8.28136 2.50244 8.00522C2.50244 7.72908 2.7263 7.50522 3.00244 7.50522H11.7955L8.89874 4.61195C8.70344 4.41641 8.70363 4.09957 8.89917 3.90427C9.0947 3.70897 9.41154 3.70916 9.60684 3.9047L13.3624 7.65582C13.5577 7.85072 13.5581 8.16705 13.3632 8.36236C13.3629 8.36261 13.3627 8.36285 13.3624 8.3631L9.60684 12.1142Z"
    />
  </svg>
);

export const gettingStartedIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4.00039 21.0999C3.40039 21.0999 2.90039 20.5999 2.90039 19.9999V13.9999V4.9999C2.90039 4.5999 3.10039 4.2999 3.40039 4.0999C4.70039 3.2999 6.20039 2.8999 8.00039 2.8999C10.3004 2.8999 11.5004 3.7999 12.6004 4.5999C13.6004 5.2999 14.4004 5.8999 16.0004 5.8999C17.4004 5.8999 18.5004 5.5999 19.4004 5.0999C19.7004 4.8999 20.1004 4.8999 20.5004 5.0999C20.8004 5.2999 21.0004 5.5999 21.0004 5.9999V14.9999C21.0004 15.3999 20.8004 15.6999 20.5004 15.8999C19.3004 16.6999 17.7004 17.0999 15.9004 17.0999C13.6004 17.0999 12.4004 16.1999 11.3004 15.3999C10.3004 14.6999 9.50039 14.0999 7.90039 14.0999C6.80039 14.0999 5.80039 14.2999 5.00039 14.6999V19.9999C5.10039 20.5999 4.60039 21.0999 4.00039 21.0999ZM8.00039 11.8999C10.3004 11.8999 11.5004 12.7999 12.6004 13.5999C13.6004 14.2999 14.4004 14.8999 16.0004 14.8999C17.1004 14.8999 18.1004 14.6999 18.9004 14.2999V7.5999C18.0004 7.8999 17.1004 8.0999 16.0004 8.0999C13.7004 8.0999 12.5004 7.1999 11.4004 6.3999C10.4004 5.6999 9.60039 5.0999 8.00039 5.0999C6.90039 5.0999 5.90039 5.2999 5.10039 5.6999V12.3999C6.00039 12.0999 6.90039 11.8999 8.00039 11.8999Z"
      fill="url(#paint0_linear)"
    />
    <defs>
      <linearGradient
        id="paint0_linear"
        x1="2.90039"
        y1="11.9959"
        x2="21.002"
        y2="11.9959"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#D1AAD7" />
        <stop offset="0.25" stopColor="#C88BC4" />
        <stop offset="0.5" stopColor="#7B8FDD" />
        <stop offset="0.7486" stopColor="#86BFF2" />
        <stop offset="0.9987" stopColor="#BBDEF2" />
      </linearGradient>
    </defs>
  </svg>
);

export const customerDashboardIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M5 18V20C5 20.6 4.6 21 4 21C3.4 21 3 20.6 3 20V18C3 17.4 3.4 17 4 17C4.6 17 5 17.4 5 18ZM20 3C19.4 3 19 3.4 19 4V20C19 20.6 19.4 21 20 21C20.6 21 21 20.6 21 20V4C21 3.4 20.6 3 20 3ZM16 8C15.4 8 15 8.4 15 9V20C15 20.6 15.4 21 16 21C16.6 21 17 20.6 17 20V9C17 8.4 16.6 8 16 8ZM8 15C7.4 15 7 15.4 7 16V20C7 20.6 7.4 21 8 21C8.6 21 9 20.6 9 20V16C9 15.4 8.6 15 8 15ZM12 12C11.4 12 11 12.4 11 13V20C11 20.6 11.4 21 12 21C12.6 21 13 20.6 13 20V13C13 12.4 12.6 12 12 12Z"
      fill="url(#paint0_linear)"
    />
    <defs>
      <linearGradient
        id="paint0_linear"
        x1="3"
        y1="11.9961"
        x2="21.0016"
        y2="11.9961"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#D1AAD7" />
        <stop offset="0.25" stopColor="#C88BC4" />
        <stop offset="0.5" stopColor="#7B8FDD" />
        <stop offset="0.7486" stopColor="#86BFF2" />
        <stop offset="0.9987" stopColor="#BBDEF2" />
      </linearGradient>
    </defs>
  </svg>
);

export const dataHostingIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M21.3 4.5001L12.3 2.1001C12.1 2.1001 11.9 2.1001 11.8 2.1001L2.8 4.5001C2.3 4.6001 2 5.0001 2 5.4001C2 12.1001 5.6 18.4001 11.5 21.8001C11.7 22.0001 11.8 22.0001 12 22.0001C12.2 22.0001 12.3 22.0001 12.5 21.9001C18.4 18.5001 22 12.2001 22 5.5001C22 5.0001 21.7 4.6001 21.3 4.5001ZM12 19.8001C7.3 16.8001 4.3 11.7001 4 6.2001L12 4.0001L20 6.2001C19.7 11.7001 16.7 16.9001 12 19.8001ZM11 12.6001L15.3 8.3001C15.7 7.9001 16.3 7.9001 16.7 8.3001C17.1 8.7001 17.1 9.3001 16.7 9.7001L11.7 14.7001C11.5 14.9001 11.3 15.0001 11 15.0001C10.7 15.0001 10.5 14.9001 10.3 14.7001L8.3 12.7001C7.9 12.3001 7.9 11.7001 8.3 11.3001C8.7 10.9001 9.3 10.9001 9.7 11.3001L11 12.6001Z"
      fill="url(#paint0_linear)"
    />
    <defs>
      <linearGradient
        id="paint0_linear"
        x1="2"
        y1="12.0458"
        x2="22.0018"
        y2="12.0458"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#D1AAD7" />
        <stop offset="0.25" stopColor="#C88BC4" />
        <stop offset="0.5" stopColor="#7B8FDD" />
        <stop offset="0.7486" stopColor="#86BFF2" />
        <stop offset="0.9987" stopColor="#BBDEF2" />
      </linearGradient>
    </defs>
  </svg>
);

export const getSupportIcon = (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M15.5 11.7C16.7 10.7 17.5 9.2 17.5 7.5C17.5 4.5 15 2 12 2C9 2 6.5 4.5 6.5 7.5C6.5 9.2 7.3 10.7 8.5 11.7C5.3 13.1 3 16.3 3 20C3 20.6 3.4 21 4 21C4.6 21 5 20.6 5 20C5 16.1 8.1 13 12 13C15.9 13 19 16.1 19 20C19 20.6 19.4 21 20 21C20.6 21 21 20.6 21 20C21 16.3 18.7 13.1 15.5 11.7ZM8.5 7.5C8.5 5.6 10.1 4 12 4C13.9 4 15.5 5.6 15.5 7.5C15.5 9.4 13.9 11 12 11C10.1 11 8.5 9.4 8.5 7.5Z"
      fill="url(#paint0_linear)"
    />
    <defs>
      <linearGradient
        id="paint0_linear"
        x1="3"
        y1="11.4959"
        x2="21.0016"
        y2="11.4959"
        gradientUnits="userSpaceOnUse"
      >
        <stop stopColor="#D1AAD7" />
        <stop offset="0.25" stopColor="#C88BC4" />
        <stop offset="0.5" stopColor="#7B8FDD" />
        <stop offset="0.7486" stopColor="#86BFF2" />
        <stop offset="0.9987" stopColor="#BBDEF2" />
      </linearGradient>
    </defs>
  </svg>
);

export const nucleusIcon = (
  <Image
    src="/static/images/icons/products/nucleus.svg"
    alt="Nucleus Icon"
    width={20}
    height={20}
    quality="100"
    priority
  />
);

export const rapidIcon = (
  <Image
    src="/static/images/icons/products/rapid.svg"
    alt="Rapid Icon"
    width={20}
    height={20}
    quality="100"
    priority
  />
);

export const studioIcon = (
  <Image
    src="/static/images/icons/products/studio.svg"
    alt="Studio Icon"
    width={20}
    height={20}
    quality="100"
    priority
  />
);

export const documentIcon = (
  <Image
    src="/static/images/icons/products/document.svg"
    alt="Document AI Icon"
    width={20}
    height={20}
    quality="100"
    priority
  />
);

export const placeholderIcon = (
  <Image
    src="/static/images/icons/scale.svg"
    alt="Scale Icon"
    width={20}
    height={20}
    quality="100"
    priority
  />
);

const iconList = {
  'Getting Started with Scale': gettingStartedIcon,
  'Customer Dashboard': customerDashboardIcon,
  'Data Hosting': dataHostingIcon,
  'Get Support': getSupportIcon,
  Nucleus: nucleusIcon,
  Rapid: rapidIcon,
  Studio: studioIcon,
  'Document AI Go': documentIcon
};

export default Accordion;
